import React, { useState } from 'react';
import { BPForm } from '@common/BPForm';
import { AuthField } from './Common/AuthField';
import { AuthLayout } from './Common/AuthLayout';
import { Button, Divider, Header, Message, Segment } from 'semantic-ui-react';
import * as yup from 'yup';
import { authClient } from './authClient';
import { ResetPasswordRequest } from '@app/api2/api';
import { AuthLayoutRow } from './Common/AuthLayoutRow';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { BPButton } from '@/components/bp/bpbutton';

interface ResetPasswordFormValues {
    email: string;
    password: string;
    confirmPassword: string;
    code: string;
}

export const ResetPassword: React.FC = () => {
    const [resetErrors, setResetErrors] = useState<string[]>([]);
    const [isSuccess, setIsSuccess] = useState(false);

    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email') || '';
    const codeParam = urlParams.get('code') || '';

    const initialValues: ResetPasswordFormValues = {
        email: emailParam,
        password: '',
        confirmPassword: '',
        code: codeParam
    };

    const validationSchema: yup.SchemaOf<ResetPasswordFormValues> = yup.object().shape({
        email: yup.string()
            .email('Invalid email address')
            .required('Email is required'),
        password: yup.string()
            .min(6, 'The password must be at least 6 characters long')
            .max(100, 'The password must be at most 100 characters long')
            .required('Password is required'),
        confirmPassword: yup.string()
            .oneOf([yup.ref('password')], 'The password and confirmation password do not match')
            .required('Confirm password is required'),
        code: yup.string()
            .required('Reset code is required')
    });

    const handleSubmit = async (values: ResetPasswordFormValues) => {
        setResetErrors([]);

        try {
            const request: ResetPasswordRequest = {
                email: values.email,
                password: values.password,
                confirmPassword: values.confirmPassword,
                code: values.code
            };

            const result = await authClient.resetPassword(request);

            if (result.success) {
                setIsSuccess(true);
            } else {
                setResetErrors(result.errors || ['Password reset failed. Please try again.']);
            }
        } catch (error) {
            console.error('Reset password error:', error);
            setResetErrors(['An error occurred during password reset. Please try again.']);
        }
    };

    if (isSuccess) {
        return (
            <AuthLayout header="Reset your password" description="Reset your password.">
                <Segment>
                    <Message success>
                        <Message.Header>Password reset successful!</Message.Header>
                        <p>Your password has been reset. You can now log in with your new password.</p>
                    </Message>
                    <div>
                        <BPButton
                            primary
                            onClick={() => window.location.assign(buildUrl(AppRoutes.Login, {}))}
                        >
                            Go to Login
                        </BPButton>
                    </div>
                </Segment>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout header="Reset your password" description="Reset your password.">
            <AuthLayoutRow>
                <Segment>
                    {resetErrors.length > 0 && (
                        <Message error>
                            <Message.Header>Please correct the following errors:</Message.Header>
                            <Message.List items={resetErrors} />
                        </Message>
                    )}

                    <BPForm
                        initialValues={initialValues}
                        vaildationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        hideButtons={true}
                    >
                        {(formProps) => (
                            <>
                                <AuthField
                                    name="email"
                                    label="Email"
                                    autoComplete="username"
                                    placeholder="name@example.com"
                                />
{/* 
                                <AuthField
                                    name="code"
                                    label="Reset Code"
                                    placeholder="Enter the reset code from your email"
                                /> */}

                                <AuthField
                                    name="password"
                                    label="Password"
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder="Enter a strong password"
                                />

                                <AuthField
                                    name="confirmPassword"
                                    label="Confirm Password"
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder="Confirm your password"
                                />

                                <Divider />

                                <BPButton
                                    className='bp-primary'
                                    type="submit"
                                    primary
                                    size="large"
                                    fluid
                                    disabled={formProps.isSubmitting}
                                    loading={formProps.isSubmitting}
                                >
                                    Reset
                                </BPButton>
                            </>
                        )}
                    </BPForm>
                </Segment>
            </AuthLayoutRow>
        </AuthLayout>
    );
};